import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Steps, message, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Box } from 'atoms';
import VendorFormV2 from 'componentsv2/forms/VendorFormV2';
import {
	getVendorByIdV2,
	updateVendorV2,
	addVendorServicabilityRouteV2,
	updateVendorServicabilityRouteV2,
	searchVendorServicabilityRoutesV2,
} from 'store/vendorSliceV2';
import { createVehicleV2, updateVehicleV2, searchVehiclesV2 } from 'store/vehicleSliceV2';
import { transformVehicleToAPIFormat } from '../create-vendorv2/CreateVendorPageV2.utils';
import {
	PageContainer,
	HeaderContainer,
	HeaderLeft,
	BackButton,
	PageSubtitle,
	StepsContainer,
	StyledSteps,
	ContentContainer,
} from './EditVendorPageV2.styled';
import { STEPS, VENDOR_LIST_ROUTE } from './EditVendorPageV2.constants';
import { transformVendorDataToFormValues } from './EditVendorPageV2.transform';
import { formatErrorMessage } from './EditVendorPageV2.helpers';
import { validateRequiredFields, validateMSMEFields } from './EditVendorPageV2.validation';
import { buildAPIDataForStep } from './EditVendorPageV2.build';

const { Step } = Steps;

const API_ERRORS_LOG_PREFIX = 'API Errors:';

export default function EditVendorPageV2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const { id } = useParams();
	const previousIdRef = useRef(null);
	const [currentStep, setCurrentStep] = useState(0);
	const [submitting] = useState(false); // Keep for backward compatibility with VendorFormV2
	const [loading, setLoading] = useState(true);
	const [initialValues, setInitialValues] = useState(null);
	const [editableStep, setEditableStep] = useState(null); // null = all disabled, number = specific step editable
	const [saving, setSaving] = useState(false); // Track save operation in progress

	/**
	 * Fetch vehicles for a vendor
	 */
	const fetchVehicles = async (vendorId) => {
		try {
			const vehiclesResponse = await dispatch(
				searchVehiclesV2({
					filter_by: { vendor_id: vendorId, is_active: true },
					pagination: { page: 1, per_page: 1000 },
				})
			);
			return vehiclesResponse?.rows && Array.isArray(vehiclesResponse.rows)
				? vehiclesResponse.rows
				: [];
		} catch (error) {
			console.error('Error fetching vehicles:', error);
			return [];
		}
	};

	/**
	 * Fetch routes for a vendor
	 */
	const fetchRoutes = async (vendorId) => {
		try {
			const routesResponse = await dispatch(
				searchVendorServicabilityRoutesV2({
					filter_by: { vendor_id: vendorId },
					pagination: { page: 1, per_page: 1000 },
				})
			);
			return routesResponse?.routes && Array.isArray(routesResponse.routes)
				? routesResponse.routes
				: [];
		} catch (error) {
			console.error('Error fetching routes:', error);
			return [];
		}
	};

	/**
	 * Transform and merge vehicles and routes into form values
	 */
	const mergeVehiclesAndRoutes = async (formValues, vehicles, routes) => {
		const { transformVehicleToFormFormat, transformRouteToFormFormat } = await import(
			'./EditVendorPageV2.transform'
		);

		const transformedVehicles = vehicles.map(transformVehicleToFormFormat).filter(Boolean);
		const transformedRoutes = routes.map(transformRouteToFormFormat).filter(Boolean);

		return {
			...formValues,
			vehicles:
				transformedVehicles.length > 0 ? transformedVehicles : formValues.vehicles || [],
			routes: transformedRoutes.length > 0 ? transformedRoutes : formValues.routes || [],
		};
	};

	// Load vendor data on mount and when id changes
	useEffect(() => {
		if (!id) {
			message.error('Vendor ID is required');
			navigate(VENDOR_LIST_ROUTE);
			return;
		}

		const vendorId = id;
		if (vendorId === undefined || vendorId === null || String(vendorId).trim() === '') {
			message.error('Invalid vendor ID');
			navigate(VENDOR_LIST_ROUTE);
			return;
		}

		// Skip if id hasn't changed (prevent unnecessary reloads)
		if (id === previousIdRef.current && previousIdRef.current !== null) {
			console.log('⏭️ [EditVendorPageV2] Skipping reload - ID unchanged:', id);
			return;
		}

		// Update ref to track current ID before loading
		previousIdRef.current = id;

		const loadVendorData = async () => {
			setLoading(true);
			// Reset state when loading new vendor
			setInitialValues(null);
			setEditableStep(null);
			setCurrentStep(0);

			try {
				console.log('🔄 [EditVendorPageV2] Loading vendor data for ID:', vendorId);

				// Fetch vendor data
				const vendorResponse = await dispatch(getVendorByIdV2({ id: vendorId }));

				if (!vendorResponse || typeof vendorResponse !== 'object') {
					console.error('❌ [EditVendorPageV2] Vendor not found for ID:', vendorId);
					message.error(
						`Vendor with ID ${vendorId} not found. Please check the vendor list.`
					);
					navigate(VENDOR_LIST_ROUTE);
					return;
				}

				// STRICT: Verify the returned vendor ID matches the requested ID
				if (vendorResponse.id !== vendorId) {
					console.error('❌ [EditVendorPageV2] Vendor ID mismatch!', {
						requestedId: vendorId,
						receivedId: vendorResponse.id,
						receivedName: vendorResponse.vendor_name,
					});
					message.error(
						`Wrong vendor loaded! Expected ID ${vendorId}, but got ID ${vendorResponse.id} (${vendorResponse.vendor_name}). This is a backend issue.`
					);
					navigate(VENDOR_LIST_ROUTE);
					return;
				}

				console.log('✅ [EditVendorPageV2] Vendor loaded:', {
					id: vendorResponse.id,
					name: vendorResponse.vendor_name,
				});

				// Fetch vehicles and routes in parallel
				const [vehicles, routes] = await Promise.all([
					fetchVehicles(vendorId),
					fetchRoutes(vendorId),
				]);

				// Transform vendor data to form values
				const formValues = transformVendorDataToFormValues(vendorResponse);

				if (!formValues || typeof formValues !== 'object') {
					message.error('Failed to load vendor data');
					navigate(VENDOR_LIST_ROUTE);
					return;
				}

				// Merge vehicles and routes
				const finalFormValues = await mergeVehiclesAndRoutes(formValues, vehicles, routes);
				setInitialValues(finalFormValues);
				console.log(
					'✅ [EditVendorPageV2] Form values set for vendor:',
					formValues.vendor_name
				);
			} catch (error) {
				console.error('❌ [EditVendorPageV2] Error loading vendor:', error);
				message.error('Failed to load vendor data');
				navigate(VENDOR_LIST_ROUTE);
			} finally {
				setLoading(false);
			}
		};

		loadVendorData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, location.pathname]);

	const handleNext = () => {
		if (currentStep < STEPS.length - 1) {
			setEditableStep(null);
			setCurrentStep(currentStep + 1);
		}
	};

	const handlePrevious = () => {
		if (currentStep > 0) {
			setEditableStep(null);
			setCurrentStep(currentStep - 1);
		}
	};

	const handleStepClick = (step) => {
		// Allow changing steps if not currently editing
		if (editableStep === null) {
			setCurrentStep(step);
		}
	};

	/**
	 * Enable editing for a specific step
	 * Each step is independently editable
	 */
	const handleEnableEdit = (step) => {
		setEditableStep(step);
	};

	/**
	 * Disable editing (cancel edit mode)
	 */
	const handleCancelEdit = () => {
		setEditableStep(null);
	};

	/**
	 * Validate form data for submission
	 */
	const validateFormData = (formValues, stepToValidate) =>
		validateRequiredFields(formValues, stepToValidate) &&
		validateMSMEFields(formValues, stepToValidate);

	// Constants for error messages
	const ERROR_MESSAGES = {
		INVALID_FORM_DATA: 'Invalid form data',
		INVALID_STEP: 'Invalid step for vendor save',
		FAILED_PREPARE_BANKING: 'Failed to prepare banking data for submission',
		FAILED_PREPARE_DATA: 'Failed to prepare data for submission',
		FAILED_UPDATE_BANK: 'Failed to update bank details',
		FAILED_UPDATE_VENDOR: 'Failed to update vendor',
		FAILED_SAVE_VENDOR: 'Failed to save vendor. Please try again.',
	};

	/**
	 * Helper: Handle banking details update response
	 */
	/**
	 * Helper: Reload vendor data from update response (faster, uses fresh data)
	 */
	const reloadVendorDataFromResponse = async (vendorData) => {
		console.log('🔄 [reloadVendorDataFromResponse] Received vendor data:', {
			hasData: !!vendorData,
			vendorId: vendorData?.id,
			vendorName: vendorData?.vendor_name,
			vendorAddress: vendorData?.vendor_address,
		});

		if (!vendorData || typeof vendorData !== 'object') {
			console.warn('⚠️ [reloadVendorDataFromResponse] Invalid vendor data, refetching...');
			// Fallback to refetch if response data is invalid
			await reloadVendorData();
			return;
		}

		try {
			const vendorId = id;
			const [vehicles, routes] = await Promise.all([
				fetchVehicles(vendorId),
				fetchRoutes(vendorId),
			]);
			const formValues = transformVendorDataToFormValues(vendorData);
			console.log('🔄 [reloadVendorDataFromResponse] Transformed form values:', {
				vendorName: formValues?.vendor_name,
				vendorAddress: formValues?.vendor_address,
				hasFormValues: !!formValues,
			});

			if (formValues && typeof formValues === 'object') {
				const finalFormValues = await mergeVehiclesAndRoutes(formValues, vehicles, routes);
				console.log('✅ [reloadVendorDataFromResponse] Setting initial values:', {
					vendorName: finalFormValues?.vendor_name,
					vendorAddress: finalFormValues?.vendor_address,
					primaryPocName: finalFormValues?.primary_poc_name,
				});
				setInitialValues(finalFormValues);

				// Small delay to ensure Formik processes the new initialValues
				// This is needed because enableReinitialize might not trigger immediately
				await new Promise((resolve) => {
					setTimeout(() => {
						resolve();
					}, 100);
				});
			} else {
				console.warn('⚠️ [reloadVendorDataFromResponse] Form values transformation failed');
			}
		} catch (error) {
			console.error('❌ [reloadVendorDataFromResponse] Error:', error);
			// Fallback to refetch on error
			await reloadVendorData();
		}
	};

	/**
	 * Helper: Reload vendor data after successful update (refetches from API)
	 */
	const reloadVendorData = async () => {
		if (!id) return;

		try {
			const vendorId = id;
			const vendorResponse = await dispatch(getVendorByIdV2({ id: vendorId }));

			if (vendorResponse && typeof vendorResponse === 'object') {
				const [vehicles, routes] = await Promise.all([
					fetchVehicles(vendorId),
					fetchRoutes(vendorId),
				]);
				const formValues = transformVendorDataToFormValues(vendorResponse);
				if (formValues && typeof formValues === 'object') {
					const finalFormValues = await mergeVehiclesAndRoutes(
						formValues,
						vehicles,
						routes
					);
					setInitialValues(finalFormValues);
				}
			}
		} catch (error) {
			console.error('Error reloading vendor data:', error);
		}
	};

	/**
	 * Helper: Update vendor info for steps 0 (Basic Info), 1 (Business Info), or 2 (Banking Info)
	 * @param {Object} formValues - Form values
	 * @param {number} step - Step number (0, 1, or 2)
	 * @param {Function} setErrors - Error setter function
	 * @param {string} successMessage - Success message to display
	 * @returns {Promise<boolean>} - True if successful, false otherwise
	 */
	const updateVendorInfoStep = async (formValues, step, setErrors, successMessage) => {
		const apiData = buildAPIDataForStep(formValues, step, id);
		if (!apiData || Object.keys(apiData).length === 0) {
			message.error(ERROR_MESSAGES.FAILED_PREPARE_DATA);
			return false;
		}

		const defaultSetErrors = (errors) => console.error(API_ERRORS_LOG_PREFIX, errors);
		const response = await dispatch(
			updateVendorV2({
				req_data: apiData,
				setErrors: setErrors || defaultSetErrors,
			})
		);

		if (response && response.status === 'success') {
			message.success(response?.message || successMessage);

			// Debug: Log response structure to diagnose update issue
			console.log('📦 [updateVendorInfoStep] Update response:', {
				hasData: !!response.data,
				dataType: typeof response.data,
				dataKeys: response.data ? Object.keys(response.data) : [],
				fullResponse: response,
			});

			// IMPORTANT: Reload data FIRST, then set editableStep to null
			// This ensures form has new values before switching to view mode
			if (response.data && typeof response.data === 'object') {
				console.log('✅ [updateVendorInfoStep] Using response.data directly');
				await reloadVendorDataFromResponse(response.data);
			} else {
				console.log('⚠️ [updateVendorInfoStep] Response.data not available, refetching...');
				await reloadVendorData();
			}

			// Set editableStep to null AFTER data is reloaded
			// This allows form to update with new values before switching to view mode
			setEditableStep(null);

			return true;
		}

		const errorMessage =
			response?.error?.message || response?.message || ERROR_MESSAGES.FAILED_UPDATE_VENDOR;
		message.error(formatErrorMessage(errorMessage));
		return false;
	};

	/**
	 * Handle Save Vendor - updates vendor for specific step
	 * Step 0 (Basic Info): Calls updateVendorV2 with step 0 data
	 * Step 1 (Business Info): Calls updateVendorV2 with step 1 data
	 * Step 2 (Banking Info): Calls updateVendorV2 with step 2 data (same as Basic and Business Info)
	 */
	const handleSaveVendor = async (formValues, setErrors) => {
		if (!formValues || typeof formValues !== 'object') {
			message.error(ERROR_MESSAGES.INVALID_FORM_DATA);
			return;
		}

		setSaving(true);
		try {
			if (!validateFormData(formValues, currentStep)) {
				setSaving(false);
				return;
			}

			if (currentStep === 0) {
				await updateVendorInfoStep(
					formValues,
					0,
					setErrors,
					'Basic information updated successfully'
				);
			} else if (currentStep === 1) {
				await updateVendorInfoStep(
					formValues,
					1,
					setErrors,
					'Business information updated successfully'
				);
			} else if (currentStep === 2) {
				// Update Banking Info - uses same updateVendorV2 API as Basic and Business Info
				await updateVendorInfoStep(
					formValues,
					2,
					setErrors,
					'Banking information updated successfully'
				);
			} else {
				message.error(ERROR_MESSAGES.INVALID_STEP);
			}
		} catch (error) {
			console.error('Error saving vendor:', error);
			const rawErrorMessage =
				error?.response?.data?.error?.message ||
				error?.response?.data?.message ||
				error?.message ||
				ERROR_MESSAGES.FAILED_SAVE_VENDOR;
			message.error(formatErrorMessage(rawErrorMessage));
		} finally {
			setSaving(false);
		}
	};

	/**
	 * Helper: Extract vehicle type ID from vehicle object
	 */
	const getVehicleTypeId = (vehicle) =>
		vehicle.vehicle_type_id ||
		vehicle.vehicleTypeId ||
		vehicle.vehicle_type ||
		vehicle.vehicleType;

	/**
	 * Helper: Create error result for vehicle save
	 */
	const createVehicleErrorResult = (vehicle, errorMessage) => ({
		success: false,
		vehicleNumber: vehicle.vehicle_number || vehicle.vehicleNumber || 'Unknown',
		error: errorMessage,
	});

	/**
	 * Helper: Extract error message from API response
	 */
	const extractVehicleErrorMessage = (response) =>
		response?.error?.message?.vehicle_type_id ||
		response?.error?.message ||
		response?.message ||
		'Failed to save vehicle';

	/**
	 * Helper: Process single vehicle save (create or update)
	 */
	const processVehicleSave = async (vehicle, vendorId, setErrors) => {
		try {
			// Validate vehicle_type_id before transformation
			const vehicleTypeId = getVehicleTypeId(vehicle);
			if (!vehicleTypeId) {
				return createVehicleErrorResult(vehicle, 'Vehicle type is required');
			}

			const vehicleData = transformVehicleToAPIFormat(vehicle, vendorId);
			const defaultSetErrors = (errors) => console.error(API_ERRORS_LOG_PREFIX, errors);
			const hasBackendId = vehicle.id && vehicle.id < 1000000000000;

			const response = hasBackendId
				? await dispatch(
						updateVehicleV2({
							req_data: { ...vehicleData, id: vehicle.id },
							setErrors: setErrors || defaultSetErrors,
						})
				  )
				: await dispatch(
						createVehicleV2({
							req_data: vehicleData,
							setErrors: setErrors || defaultSetErrors,
						})
				  );

			if (response && response.status === 'success') {
				return {
					success: true,
					vehicleNumber: vehicle.vehicle_number || vehicle.vehicleNumber,
					vehicleId: response?.data?.id || vehicle.id,
				};
			}

			return createVehicleErrorResult(vehicle, extractVehicleErrorMessage(response));
		} catch (error) {
			// Handle transformation errors (e.g., missing vehicle_type_id)
			if (error.message && error.message.includes('vehicle_type_id')) {
				return createVehicleErrorResult(vehicle, 'Vehicle type is required');
			}

			const errorMsg =
				error?.response?.data?.error?.message ||
				error?.response?.data?.message ||
				error?.message ||
				'Unknown error';
			return createVehicleErrorResult(vehicle, errorMsg);
		}
	};

	/**
	 * Helper: Show vehicle save results
	 */
	const showVehicleSaveResults = (processedResults, totalCount) => {
		const successCount = processedResults.filter((r) => r.success).length;
		const failedCount = totalCount - successCount;

		if (successCount === totalCount) {
			message.success(`All ${successCount} vehicle(s) saved successfully`);
		} else if (successCount > 0) {
			message.warning(`${successCount} vehicle(s) saved, ${failedCount} failed`);
		} else {
			message.error('Failed to save any vehicles. Please check the errors and try again.');
		}
	};

	/**
	 * Handle Save Vehicles (creates/updates vehicles but stays on page)
	 * Called from step 3 (Vehicle Information)
	 */
	const handleSaveVehicles = async (formValues, setErrors, setFieldValue) => {
		const vehicles = formValues?.vehicles || [];
		if (vehicles.length === 0) {
			message.warning('Please add at least one vehicle before saving');
			return;
		}

		setSaving(true);
		try {
			const vehiclePromises = vehicles.map((vehicle) =>
				processVehicleSave(vehicle, id, setErrors)
			);

			const results = await Promise.allSettled(vehiclePromises);
			const processedResults = results.map((result) =>
				result.status === 'fulfilled' ? result.value : { success: false }
			);

			// Update vehicles array with backend IDs
			const updatedVehicles = vehicles.map((vehicle, index) => {
				const result = processedResults[index];
				return result?.success && result?.vehicleId
					? { ...vehicle, id: result.vehicleId }
					: vehicle;
			});

			if (setFieldValue && typeof setFieldValue === 'function') {
				setFieldValue('vehicles', updatedVehicles);
			}

			showVehicleSaveResults(processedResults, vehicles.length);
		} catch (error) {
			console.error('Error saving vehicles:', error);
			message.error('An unexpected error occurred during vehicle save');
		} finally {
			setSaving(false);
		}
	};

	/**
	 * Helper: Process single route save (create or update)
	 */
	/**
	 * Helper: Extract route data from route object (handles both camelCase and snake_case)
	 */
	const getRouteData = (route) => ({
		type_of_movement: route.type_of_movement || route.typeOfMovement || '',
		source_city: route.source_city || route.sourceCity || '',
		destination_city: route.destination_city || route.destinationCity || '',
	});

	/**
	 * Helper: Create error result for route save
	 */
	const createRouteErrorResult = (route, errorMessage) => {
		const routeData = getRouteData(route);
		const routeLabel = `${routeData.source_city} → ${routeData.destination_city}`;
		return {
			success: false,
			route: routeLabel || 'Unknown route',
			error: errorMessage,
		};
	};

	/**
	 * Helper: Process single route save (create or update)
	 */
	const processRouteSave = async (route, vendorId, setErrors) => {
		try {
			const routeData = getRouteData(route);

			// Validate required fields
			if (
				!routeData.type_of_movement ||
				!routeData.source_city ||
				!routeData.destination_city
			) {
				return createRouteErrorResult(
					route,
					'Type of movement, source city, and destination city are required'
				);
			}

			const defaultSetErrors = (errors) => console.error(API_ERRORS_LOG_PREFIX, errors);
			const hasBackendId = route.id && route.id < 1000000000000;
			const routeLabel = `${routeData.source_city} → ${routeData.destination_city}`;

			const response = hasBackendId
				? await dispatch(
						updateVendorServicabilityRouteV2({
							id: route.id,
							type_of_movement: routeData.type_of_movement,
							source_city: routeData.source_city,
							destination_city: routeData.destination_city,
							setErrors: setErrors || defaultSetErrors,
						})
				  )
				: await dispatch(
						addVendorServicabilityRouteV2({
							vendor_id: vendorId,
							type_of_movement: routeData.type_of_movement,
							source_city: routeData.source_city,
							destination_city: routeData.destination_city,
							setErrors: setErrors || defaultSetErrors,
						})
				  );

			if (response && response.status === 'success') {
				return {
					success: true,
					route: routeLabel,
					routeId: response?.data?.id || route.id,
				};
			}

			const errorMessage =
				response?.error?.message || response?.message || 'Failed to save route';
			return createRouteErrorResult(route, errorMessage);
		} catch (error) {
			const errorMsg =
				error?.response?.data?.error?.message ||
				error?.response?.data?.message ||
				error?.message ||
				'Unknown error';
			return createRouteErrorResult(route, errorMsg);
		}
	};

	/**
	 * Helper: Show route save results
	 */
	const showRouteSaveResults = (processedResults, totalCount) => {
		const successCount = processedResults.filter((r) => r.success).length;
		const failedCount = totalCount - successCount;

		if (successCount === totalCount) {
			message.success(`All ${successCount} route(s) saved successfully`);
		} else if (successCount > 0) {
			message.warning(`${successCount} route(s) saved, ${failedCount} failed`);
		} else {
			message.error('Failed to save any routes. Please check the errors and try again.');
		}
	};

	/**
	 * Handle Save Routes (creates/updates routes but stays on page)
	 * Called from step 4 (Serviceability Info)
	 */
	const handleSaveRoutes = async (formValues, setErrors, setFieldValue) => {
		const routes = formValues?.routes || [];
		if (routes.length === 0) {
			message.warning('Please add at least one route before saving');
			return;
		}

		setSaving(true);
		try {
			const routePromises = routes.map((route) => processRouteSave(route, id, setErrors));

			const results = await Promise.allSettled(routePromises);
			const processedResults = results.map((result) =>
				result.status === 'fulfilled' ? result.value : { success: false }
			);

			// Update routes array with backend IDs
			const updatedRoutes = routes.map((route, index) => {
				const result = processedResults[index];
				return result?.success && result?.routeId
					? { ...route, id: result.routeId }
					: route;
			});

			if (setFieldValue && typeof setFieldValue === 'function') {
				setFieldValue('routes', updatedRoutes);
			}

			showRouteSaveResults(processedResults, routes.length);
		} catch (error) {
			console.error('Error saving routes:', error);
			message.error('An unexpected error occurred during route save');
		} finally {
			setSaving(false);
		}
	};

	/**
	 * Handle Save - routes to appropriate save handler based on current step
	 */
	const handleSave = async (formValues, setErrors, setFieldValue) => {
		if (currentStep <= 2) {
			// Steps 0-2: Save vendor
			await handleSaveVendor(formValues, setErrors);
		} else if (currentStep === 3) {
			// Step 3: Save vehicles
			await handleSaveVehicles(formValues, setErrors, setFieldValue);
		} else if (currentStep === 4) {
			// Step 4: Save routes
			await handleSaveRoutes(formValues, setErrors, setFieldValue);
		}
	};

	const handleSubmit = async (formValues) => {
		if (submitting) return;

		if (!formValues || typeof formValues !== 'object') {
			message.error('Invalid form data');
			return;
		}

		// Navigate to vendor list (existing behavior)
		navigate(VENDOR_LIST_ROUTE);
	};

	if (loading) {
		return (
			<PageContainer>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
					<Spin size="large" tip="Loading vendor data..." />
				</Box>
			</PageContainer>
		);
	}

	if (!initialValues) {
		return null;
	}

	return (
		<PageContainer>
			<HeaderContainer>
				<HeaderLeft>
					<BackButton
						type="link"
						icon={<ArrowLeftOutlined />}
						onClick={() => navigate(VENDOR_LIST_ROUTE, { state: { refresh: true } })}
					>
						Edit Vendor
					</BackButton>
					<PageSubtitle>Update vendor information</PageSubtitle>
				</HeaderLeft>
			</HeaderContainer>

			<StepsContainer>
				<StyledSteps current={currentStep} onChange={handleStepClick}>
					{STEPS.map((step) => (
						<Step key={step.key} title={step.title} />
					))}
				</StyledSteps>
			</StepsContainer>

			<ContentContainer>
				<VendorFormV2
					currentStep={currentStep}
					onNext={handleNext}
					onPrevious={handlePrevious}
					onSubmit={handleSubmit}
					totalSteps={STEPS.length}
					submitting={submitting}
					initialValues={initialValues}
					isEditMode
					editableStep={editableStep}
					onEnableEdit={handleEnableEdit}
					onCancelEdit={handleCancelEdit}
					vendorId={id}
					onSave={handleSave}
					saving={saving}
					savedVendorId={id}
					vehiclesSaved
					routesSaved
				/>
			</ContentContainer>
		</PageContainer>
	);
}
